import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { ReactElement, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import smallAvatar from '~/ui/assets/images/smallAvatar.svg';
import variables from '~/ui/assets/styles/colors.module.scss';

import { ReactComponent as GlobeIcon } from '~/ui/assets/images/globe.svg';
import PrivateImage from '~/ui/components/common/PrivateImage';
import { mergeAllocationByUserId } from '~/utils/clientAllocation';
import extractDateFromString from '~/utils/date/extractDateFromString';
import getIsGlobalUser from '~/utils/roles/getIsGlobalUser';
import groupAllocationClients from './helpers/groupAllocationClients';

import EditIcon from '~/ui/assets/images/editWhiteSecond.svg';
import Breadcrumbs from '~/ui/components/common/Breadcrumbs';
import Loader from '~/ui/components/common/Loader';
import { DUPLICATE_CLIENT_ALLOCATION, EDIT_CLIENT_ALLOCATION, NETWORK } from '~/ui/constants/paths';

import useGetClientAllocations from '../../hooks/useGetClientAllocations';
import ClientCell from './components/ClientsCell';
import LocationGroupClient from './components/LocationGroupClient';

import styles from './ViewClientAllocation.module.scss';

type IParams = {
  actTeamId: string;
  clientAllocationDate: string;
};

const ViewClientAllocation = (): ReactElement => {
  const { actTeamId, clientAllocationDate } = useParams<IParams>();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { clinic: userClinic } = useStoreState(state => state.user.current);
  const actTeam = useStoreState(state => state.actTeam.current);
  const { isArchived } = actTeam || {};

  const { getClientAllocations, loading, clientAllocations, locationGroups } =
    useGetClientAllocations();

  const { isActTeam, isActTeamMember, isGlobalUser } = useRole();

  const hasOwnAllocation = clientAllocations?.some(item => item.isOwn && !item.isArchived);

  useEffect(() => {
    const allocationDate = `${clientAllocationDate}T00:00:00Z`;

    getClientAllocations({ actTeamId, allocationDate });
  }, [getClientAllocations, actTeamId, clientAllocationDate]);

  const filteredAllocations = useMemo(
    () => (clientAllocations || []).filter(a => !a.isArchived),
    [clientAllocations],
  );

  const allAllocationsAreResolved = useMemo(
    () => filteredAllocations.every(item => !!item.resolved),
    [filteredAllocations],
  );

  if (loading || (!actTeam && !isGlobalUser) || !clientAllocations) return <Loader />;

  const breadcrumbItems = [
    ...(isGlobalUser ? [{ to: NETWORK, title: `${userClinic.name} Teams` }] : []),
    ...(isActTeam
      ? [
          {
            title: actTeam.name,
            to: {
              pathname: NETWORK,
            },
          },
        ]
      : []),
    ...(!isActTeam && !isGlobalUser ? [{ to: NETWORK, title: `${userClinic.name} Teams` }] : []),
    { to: pathname, title: 'Client Allocation' },
  ];

  return (
    <div>
      <div className={styles.header}>
        <div>
          <h2>{extractDateFromString(clientAllocations[0]?.date)}</h2>
          <Breadcrumbs itemsForBreadcrumbs={breadcrumbItems} />
        </div>
        {!isArchived && !allAllocationsAreResolved && (
          <>
            <div>
              <Button
                color="primary"
                variant="outlined"
                className={styles.buttonWrapper}
                disabled={(isActTeamMember || isGlobalUser) && !hasOwnAllocation}
                onClick={() =>
                  navigate(
                    DUPLICATE_CLIENT_ALLOCATION.replace(':actTeamId', actTeamId).replace(
                      ':clientAllocationDate',
                      String(clientAllocations[0].date.split('T')[0]),
                    ),
                  )
                }
              >
                Duplicate
              </Button>
              <Button
                color="primary"
                variant="contained"
                startIcon={<img src={EditIcon} alt="edit" />}
                disabled={(isActTeamMember || isGlobalUser) && !hasOwnAllocation}
                onClick={() =>
                  navigate(
                    EDIT_CLIENT_ALLOCATION.replace(':actTeamId', actTeamId).replace(
                      ':clientAllocationDate',
                      String(clientAllocations[0].date.split('T')[0]),
                    ),
                  )
                }
              >
                Edit Client Allocation
              </Button>
            </div>
          </>
        )}
      </div>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Team Members</TableCell>
              <TableCell>Clients</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mergeAllocationByUserId(filteredAllocations).map(allocation => {
              if (allocation.isArchived) {
                return null;
              }

              const allocationClients = groupAllocationClients(
                locationGroups,
                allocation.user.clients,
              );

              const hasGlobalUserRole = getIsGlobalUser(allocation.user.roles, userClinic?.id);

              return (
                <TableRow key={allocation.id}>
                  <TableCell>
                    <Box className={styles.nameCell}>
                      <div className={styles.photoContainer}>
                        <PrivateImage
                          className={styles.image}
                          src={allocation.user?.photo || smallAvatar}
                          alt="avatar"
                        />
                        {hasGlobalUserRole && (
                          <div className={styles.absoluteIcon}>
                            <GlobeIcon width={14} height={14} color={variables.colorGreenGlobal} />
                          </div>
                        )}
                      </div>
                      <Typography className={styles.name}>{allocation.user.name}</Typography>
                      <Typography variant="body2" className={styles.subRoles}>
                        {allocation.user.subRoles.map(subRole => subRole.name).join(', ')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {!!allocation.crisisClients?.length && (
                      <ClientCell
                        clients={allocation.crisisClients}
                        isDispatch
                        resolved={allocation.resolved}
                      />
                    )}
                    {allocation.locationGroups?.length ? (
                      <LocationGroupClient
                        groups={allocationClients.filter(group => group.clients?.length)}
                        allocationId={allocation.id}
                      />
                    ) : (
                      !!allocation.user.clients.length && (
                        <ClientCell clients={allocation.user.clients} isDispatch={false} />
                      )
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ViewClientAllocation;
